import { HTTPError, sendHTTPRequest } from '@/util/http'
import type { StandardSetRef } from '@/standards/standard_types'
import type { AccessPointLevel } from '@/util/access_points'
import type { ObjectiveLevel } from '@/goals/objectives/objective_levels'
import type { SubjectAlignment } from './SelectSubjects.vue'

const toolkitUrl = import.meta.env.VITE_TOOLKIT_URL

export interface EditableObjective {
  level: ObjectiveLevel
  title: string
  text: string
  uuid?: string
  resources?: string[]
  created_at?: string
}

export interface EditableAccessPoint {
  uuid: string
  access_point_level: AccessPointLevel
  author_id?: string
  goal_text: string
  goal_resources: string[]
  resource_languages?: string[]
  objectives?: EditableObjective[]
  objective_resources?: Record<string, string[]>
  assessment_items?: Record<string, unknown[]>
  updated_at: string
}

export interface EditableAnchorPage extends EditableAccessPoint {
  title: string
  subject_alignments: SubjectAlignment[]
  grades: string[]
  adaptation_strategies: string[]
  published: boolean
  score: number | null
  standard_set_refs: StandardSetRef[]
  universal: boolean | null
  characteristics?: string[]
  access_grade_level_uuid?: string
  access_points: EditableAccessPoint[]
}

export interface DraftStatus {
  draft: boolean
  published_at: string
}

export type SaveResponse = Record<string, boolean | string> & { updated_at?: string }

const anchorUrl = (path: string) => `${toolkitUrl}/api/anchor-access-points${path}`

// Vue 2 accepted a bare uuid from create as well as a JSON string.
function parseJsonOrText(body: string): unknown {
  try {
    return JSON.parse(body)
  } catch {
    return body
  }
}

// The server explains a refusal (for example a failed role check) in the body; show that, not just the status.
async function requestJson<T>(url: string, init: { method?: string, jsonBody?: Record<string, unknown> } = {}): Promise<T> {
  try {
    const response = await sendHTTPRequest({ url, ...init })
    const body = (await response.text()).trim()
    return (body ? parseJsonOrText(body) : undefined) as T
  } catch (error) {
    if (!(error instanceof HTTPError)) throw error
    const message = await error.response.text().catch(() => '')
    throw new Error(message || error.message, { cause: error })
  }
}

export function getAnchorPageForEditing(gradeLevelUuid: string): Promise<EditableAnchorPage> {
  return requestJson(`${anchorUrl(`/${gradeLevelUuid}`)}?with_access_points=1&prefer_draft=1`)
}

export function getDraftStatus(gradeLevelUuid: string): Promise<DraftStatus> {
  return requestJson(anchorUrl(`/draft/${gradeLevelUuid}`))
}

export function publishDraft(gradeLevelUuid: string): Promise<unknown> {
  return requestJson(anchorUrl(`/overwrite-draft/${gradeLevelUuid}`), { method: 'PUT', jsonBody: {} })
}

export function deleteDraft(gradeLevelUuid: string): Promise<unknown> {
  return requestJson(anchorUrl(`/draft/${gradeLevelUuid}`), { method: 'DELETE' })
}

export function createAnchorPage(title: string): Promise<string> {
  return requestJson(anchorUrl(''), { method: 'POST', jsonBody: { title } })
}

export function generateAccessPoints(gradeLevelUuid: string): Promise<unknown> {
  return requestJson(anchorUrl(`/${gradeLevelUuid}`), { method: 'POST', jsonBody: {} })
}

// The role check only counts the page as the user's own draft when the update names its author.
export function updateAccessPoints(gradeLevelUuid: string, uuids: string[], updates: Record<string, unknown>, authorId: string): Promise<SaveResponse> {
  return requestJson(anchorUrl(`/${gradeLevelUuid}`), { method: 'PUT', jsonBody: { uuids, updates: { ...updates, author_id: authorId } } })
}
